import { useEffect, useRef, useState } from 'react'
import './styles.css'
import { resolveLocation } from '@astro/vedastro'
import BirthDetailsForm from './BirthDetailsForm'
import { STUDIO_PARTS } from './studioParts'
import { StudioEmpty } from './StudioBits'
import SavedKundlis from './SavedKundlis'
import DownloadKundliButton from './DownloadKundliButton'
import { savedKey, savedToBirth, useSavedKundlis } from './useSavedKundlis'

function parseDate(str) {
  if (!str) return null
  const d = new Date(`${str}T12:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

function fmtDate(str) {
  const d = parseDate(str)
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : str
}

/** Two-letter monogram from a name, or a planet glyph when unnamed. */
function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return null
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
}

/** Stable identity for a set of raw birth fields — guards seed effects. */
function birthKey(b) {
  return b ? `${b.dob}|${b.tob || ''}|${b.pob || b.place || ''}|${b.name || ''}` : ''
}

/**
 * Kundli Studio — divisional charts, calculations, dashas, strengths, yogas
 * and the full life report from one set of birth details. Parts fetch their
 * VedAstro data lazily and the package memoises per input, so switching tabs
 * never repeats a paid API call.
 *
 * Three usage modes (user-approved API):
 * - No props (website): the built-in birth form drives everything.
 * - `initialBirth` (seeker dashboard): auto-casts from the saved profile
 *   details on mount; the form stays tucked behind "Cast for Someone Else".
 * - `birth` (astrologer dashboard): fully controlled — the host's own client
 *   picker supplies `{ name, dob, tob, pob }` and the built-in form is hidden.
 *
 * `loading` tells the studio the host is still fetching those details, so it
 * shows the loading banner instead of flashing the empty birth form.
 *
 * Hosts that list the saved history on their own screen (the seeker
 * dashboard's Saved Kundlis sub-tab) pass `reopen` — a saved row, re-cast
 * whenever a new object arrives — and `savedTab`, which drops the studio's
 * own "My Kundlis" button and inline list. Casts are still saved.
 */
export default function KundliStudio({
  birth: controlled = null,
  initialBirth = null,
  loading = false,
  reopen = null,
  savedTab = false,
}) {
  const [birth, setBirth] = useState(null) // resolved active birth (with Location)
  const [resolving, setResolving] = useState(false)
  const [notice, setNotice] = useState('')
  const [partKey, setPartKey] = useState('charts')
  const [showForm, setShowForm] = useState(!controlled && !initialBirth)
  const [showSaved, setShowSaved] = useState(false)
  const seedKeyRef = useRef('')
  const kundliHistory = useSavedKundlis()
  // Where this cast came from — the saved history row records it.
  const source = controlled ? 'astrologer_dashboard' : initialBirth ? 'seeker_dashboard' : 'website'

  const generate = async (form, { fromSeed = false } = {}) => {
    if (!parseDate(form.dob)) {
      // A bad host-supplied seed isn't the user's doing — fall back to the
      // form quietly instead of flashing a validation error at them.
      if (!fromSeed) setNotice('Please enter a valid date of birth.')
      setShowForm(!controlled)
      return false
    }
    // A valid seed may arrive AFTER mount (profile query still loading when
    // the studio first rendered) — collapse the form the moment it lands so
    // seekers see their own chart casting, not an empty form.
    if (fromSeed && !controlled) setShowForm(false)
    setResolving(true)
    setNotice('')
    try {
      // A picked autocomplete result already carries exact coordinates — use
      // them directly; only fall back to geocoding a bare place string.
      const location = form.location || (await resolveLocation(form.pob ?? form.place))
      const tob = /^\d{1,2}:\d{2}/.test(form.tob || '') ? form.tob.slice(0, 5) : '12:00'
      const cast = { name: form.name || '', gender: form.gender || '', dob: form.dob, tob, location, srcKey: birthKey(form) }
      setBirth(cast)
      // Every chart a signed-in user casts is kept (re-casting a saved one just
      // bumps it to the top) — except the seeker's own profile chart, which
      // already lives on their profile.
      const ownChart = initialBirth && birthKey(form) === birthKey(initialBirth)
      if (!ownChart) kundliHistory.saveBirth(cast, { source, bookingId: form.bookingId })
      return true
    } catch {
      setNotice('We could not look up that place right now. Please try again.')
      return false
    } finally {
      setResolving(false)
    }
  }

  // Host-supplied details: controlled `birth` wins over the one-time seed.
  const ckey = birthKey(controlled)
  const ikey = birthKey(initialBirth)
  useEffect(() => {
    const seed = controlled || initialBirth
    const key = ckey || ikey
    if (!seed || !key || key === seedKeyRef.current) return
    seedKeyRef.current = key
    generate(seed, { fromSeed: true })
  }, [ckey, ikey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reopen a saved kundli: re-cast live from its stored inputs + coordinates.
  const openSaved = async (row) => {
    setShowSaved(false)
    const ok = await generate(savedToBirth(row))
    if (ok && initialBirth) setShowForm(false)
  }

  // A saved row picked on the host's own history screen.
  useEffect(() => {
    if (reopen) openSaved(reopen)
  }, [reopen]) // eslint-disable-line react-hooks/exhaustive-deps

  const submitForm = async (form) => {
    const ok = await generate(form)
    // With a saved chart to fall back on, tuck the form away after success.
    if (ok && initialBirth) setShowForm(false)
  }

  // A seed that has arrived but whose cast effect hasn't run yet (one render).
  const seedPending = Boolean(ckey || ikey) && (ckey || ikey) !== seedKeyRef.current
  const formVisible = !controlled && showForm && !loading && !seedPending
  // Host still fetching the details, or the place is being looked up.
  const casting = loading || seedPending || (resolving && !formVisible)
  const onOwnChart = initialBirth && birth && birth.srcKey === birthKey(initialBirth)
  const displayName = (birth?.name || '').trim() || (controlled ? 'Client' : 'Seeker')
  const mono = initials(birth?.name)
  const chips = birth
    ? [
        { icon: '📅', text: fmtDate(birth.dob) },
        { icon: '⏰', text: birth.tob },
        { icon: '📍', text: birth.location?.Name },
      ].filter((c) => c.text)
    : []

  const activeSavedKey = birth
    ? savedKey({ dob: birth.dob, tob: birth.tob, lat: birth.location?.Latitude, lon: birth.location?.Longitude, name: birth.name })
    : ''
  // History lives with the signed-in account; the astrologer console renders
  // its own copy of the list next to its client picker, and the seeker
  // dashboard has its own Saved Kundlis sub-tab (`savedTab`).
  const canSave = !controlled && !savedTab && kundliHistory.signedIn
  const savedToggle = canSave && (
    <button
      type="button"
      className={`ks-ghost-btn${showSaved ? ' is-on' : ''}`}
      onClick={() => setShowSaved((v) => !v)}
      aria-expanded={showSaved}
      title="Every kundli you cast is saved automatically — reopen any of them here"
    >
      🕘 My Kundlis{kundliHistory.total > 0 ? ` (${kundliHistory.total})` : ''}
    </button>
  )

  const part = STUDIO_PARTS.find((p) => p.key === partKey)
  const ActivePart = part?.Component

  const loadingBanner = (
    <div className="ks-banner ks-banner--loading" role="status" aria-busy="true" aria-live="polite">
      <div className="ks-banner-id">
        <span className="ks-banner-avatar ks-banner-avatar--loading" aria-hidden="true">
          <span className="ks-banner-spinner" />
        </span>
        <div className="ks-banner-idtext">
          <span className="ks-banner-eyebrow">{loading ? 'Loading your details' : 'Casting the chart'}</span>
          <span className="ks-skel ks-pulse ks-banner-skel-name" aria-hidden="true" />
          <span className="ks-sr-only">Loading your Kundli…</span>
        </div>
      </div>
      <div className="ks-banner-chips" aria-hidden="true">
        {[88, 64, 168].map((w) => (
          <span key={w} className="ks-skel ks-pulse ks-banner-skel-chip" style={{ width: `${w}px` }} />
        ))}
      </div>
    </div>
  )

  const banner = (
    <div className="ks-banner">
      <div className="ks-banner-id">
        <span className="ks-banner-avatar">{mono || '🪐'}</span>
        <div className="ks-banner-idtext">
          <span className="ks-banner-eyebrow">Studio open for</span>
          <h4 className="ks-banner-name">{resolving && !birth ? 'Casting the chart…' : displayName}</h4>
        </div>
      </div>
      {chips.length > 0 && (
        <div className="ks-banner-chips">
          {chips.map((c) => (
            <span className="ks-banner-chip" key={c.icon}>
              <span className="ks-banner-chip-ico" aria-hidden="true">{c.icon}</span>
              {c.text}
            </span>
          ))}
        </div>
      )}
      {!controlled && birth && (
        <div className="ks-banner-actions">
          {savedToggle}
          {initialBirth && !onOwnChart && (
            <button type="button" className="ks-ghost-btn" onClick={() => generate(initialBirth)}>↩ My Chart</button>
          )}
          <button type="button" className="ks-ghost-btn ks-ghost-btn--solid" onClick={() => setShowForm(true)}>
            Cast for Someone Else
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div className="kundli-studio ks-shell">
      <div className="ks-head-card">
        {formVisible ? (
          <div className="ks-head-form">
            <BirthDetailsForm onGenerate={submitForm} busy={resolving} />
            {(savedToggle || (!controlled && !kundliHistory.signedIn && !kundliHistory.authLoading)) && (
              <div className="ks-saved-bar">
                {savedToggle || (
                  <p className="ks-saved-hint">
                    <button type="button" className="ks-saved-signin" onClick={() => kundliHistory.login()}>Sign in</button>{' '}
                    to keep every kundli you cast and reopen it anytime.
                  </p>
                )}
              </div>
            )}
            {initialBirth && birth && (
              <button type="button" className="ks-linkbtn" onClick={() => setShowForm(false)}>
                ← Back to the open chart
              </button>
            )}
            {birth && !initialBirth && (
              <p className="ks-form-open">
                Studio open for <strong>{displayName}</strong>
                <span className="ks-form-open-meta"> · {chips.map((c) => c.text).join(' · ')}</span>
              </p>
            )}
          </div>
        ) : casting ? (
          loadingBanner
        ) : (
          banner
        )}
        {canSave && showSaved && <SavedKundlis onOpen={openSaved} activeKey={activeSavedKey} />}
        {notice && <p className="ks-notice">{notice}</p>}

        {/* Section tabs + the full-report download, pinned at the rail's end. */}
        <div className="ks-railbar">
          <div className="ks-tabrail" role="tablist" aria-label="Kundli Studio sections">
            {STUDIO_PARTS.map((p) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={partKey === p.key}
                className={`ks-tab${partKey === p.key ? ' active' : ''}${p.soon ? ' ks-tab-soon' : ''}`}
                onClick={() => !p.soon && setPartKey(p.key)}
                disabled={p.soon}
              >
                <span className="ks-tab-ico">{p.icon}</span>
                {p.title}
                {p.soon && <span className="ks-soon-chip">Soon</span>}
              </button>
            ))}
          </div>
          {birth && <DownloadKundliButton birth={birth} />}
        </div>
      </div>

      <div className="ks-panel">
        <div className="ks-panel-head">
          <span className="ks-panel-icon">{part?.icon}</span>
          <div>
            <h3>{part?.title}</h3>
            <p>{part?.desc}</p>
          </div>
        </div>
        {birth && ActivePart ? (
          <ActivePart birth={birth} />
        ) : (
          <StudioEmpty
            icon="🪐"
            text={
              casting || resolving || (controlled && !notice)
                ? 'Casting the chart — the studio opens in a moment…'
                : 'Enter your birth details above to unlock your full chart studio — divisional charts, planetary tables and more.'
            }
          />
        )}
      </div>
    </div>
  )
}
